import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ProjectUtilsService } from '../../services/project-utils.service';
import { Partner, PostResponse, ToastConfig } from '@app/shared';
import {
  deepClean,
  disabledEndDate,
  disabledStartDate,
  DrawerEscapeBlockerService,
  ProjectService,
} from '@app/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { finalize } from 'rxjs';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-create-project-drawer',
  standalone: false,

  templateUrl: './create-project-drawer.component.html',
  styleUrl: './create-project-drawer.component.less',
})
export class CreateProjectDrawerComponent implements OnInit, OnDestroy {
  @Output() createEvent = new EventEmitter<any>();

  nzData: {
    opportunityId?: number;
    leadId?: number;
    opportunity?: any;
    type?: string;
  } = inject(NZ_DRAWER_DATA) || {};

  projectForm!: FormGroup;
  disabledEndDate = disabledEndDate;
  disabledStartDate = disabledStartDate;
  deepClean = deepClean;

  partnerlist: Partner[] = [];
  mojitoEmployeeList: Partner[] = [];
  cecEmployeeList: Partner[] = [];
  clientsList: Partner[] = [];
  driversList: Partner[] = [];
  forwarderList: Partner[] = [];
  providerList: Partner[] = [];

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Proyecto creado correctamente.',
    showUndo: false,
  };

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;

  creatingProject: boolean = false;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private fb: FormBuilder,
    public ps: ProjectUtilsService,
    private notificationService: NzNotificationService,
    private projectService: ProjectService,
    public globalValuesService: GlobalValuesService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService
  ) {
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value) {
          this.cecEmployeeList = this.globalValuesService.cecEmployeeList;
          this.clientsList = this.globalValuesService.clientsList;
        }
      });
  }

  ngOnInit(): void {
    this.projectForm = this.fb.group({
      name: ['', [Validators.required]],
      cotizacion: [''],
      ejecutivoOperaciones: [null],
      gerenteAdministracion: [null],
      subgerenteProyectos: [null],
      gerenteOperaciones: [null],
      date: [null],
      endDate: [null],
    });

    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  createProject(): void {
    if (this.projectForm.valid) {
      this.creatingProject = true;
      // Aqui se haria la peticion al back
      const payload: any = {
        name: this.projectForm.value.name,
        odooEntityId: this.projectForm.value.cotizacion,
        operationExecutiveId: this.projectForm.value.ejecutivoOperaciones,
        deputyProjectManagerId: this.projectForm.value.subgerenteProyectos,
        administrationAgentId: this.projectForm.value.gerenteAdministracion,
        operationManagerId: this.projectForm.value.gerenteOperaciones,
        status: 'NonValidated',
        reference: Date.now().toString(), // Esta es una referencia por default, despues se puede cambiar
      };

      // Solo incluir las fechas si tienen valor
      if (this.projectForm.value.date) {
        payload.startPlanDate = this.projectForm.value.date.toISOString();
      }
      if (this.projectForm.value.endDate) {
        payload.endPlanDate = this.projectForm.value.endDate.toISOString();
      }

      // Incluir leadId si está disponible (cuando se crea desde una oportunidad/lead)
      if (this.nzData?.leadId) {
        payload.leadId = this.nzData.leadId;
      }

      const cleanPayload = this.deepClean(payload);

      this.projectService
        .createProject(cleanPayload)
        .pipe(
          finalize(() => {
            this.creatingProject = false;
            this.close();
            this.notRef = this.notificationService.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' }
            );
          })
        )
        .subscribe({
          next: (rsp: PostResponse) => {
            if (rsp.statusCode === 200) {
              this.ps.projects.push(rsp.data);
              this.createEvent.emit(rsp.data);
            } else {
              this.toastConfig = {
                icon: 'dangerous',
                iconColor: '#CB2A2F',
                content: 'Error al crear el proyecto.',
                showUndo: false,
              };
            }
          },
          error: (e) => {
            console.error(e);
            this.toastConfig = {
              icon: 'dangerous',
              iconColor: '#CB2A2F',
              content: 'Error al crear el proyecto.',
              msg: e.error.message,
              showUndo: false,
            };
          },
        });
    } else {
      console.log('Formulario inválido, revisa los errores');
      this.projectForm.markAllAsTouched();
    }
  }

  getControl(value: string): FormControl {
    console.log(this.projectForm!.get(value));
    return this.projectForm!.get(value)! as FormControl;
  }

  close(): void {
    this.projectForm.reset();
    this.drawerRef.close();
  }

  ngOnDestroy(): void {
    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.unblock();
  }
}
